import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  MenuList,
  TextField,
  Typography,
  type MenuItemProps,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type SyntheticEvent,
} from 'react';
import CategoryChip from '~/components/CategoryChip';
import { LazyImage } from '~/components/LazyImage';
import ReportChip from '~/components/ReportChip';
import { useDebounce } from '~/hooks/useDebounce';
import useRouter from '~/hooks/useRouter';
import { useSuggestions } from '~/hooks/useSuggestions';
import useSuggestionsQuery from '~/hooks/useSuggestionsQuery';
import { MAX_SEARCH_LENGTH, MIN_SEARCH_LENGTH } from '~/scripts/constants';
import { Searches } from '~/types/searches';
import { Suggestions } from '~/types/suggestions';
import type { ReportsDropdownProps } from './types';

export const ReportsDropdown: React.FC<ReportsDropdownProps> = ({
  value: controlledValue,
  label,
  placeholder,
  reportsOnly = false,
  operator,
  onChange,
  onBlur,
  onKeyUp,
  loading,
  TextFieldProps,
  autoFocus,
  excludedReports,
  ...autoCompleteProps
}) => {
  const suggestions = useSuggestions();
  const [focused, setFocused] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [uncontrolledValue, setUncontrolledValue] = useState<
    Searches.ISearchItem[]
  >([]);

  // Use controlled value if provided, otherwise use uncontrolled value
  const value = controlledValue ?? uncontrolledValue;

  const allSuggestions = useMemo(
    () =>
      (suggestions ?? []).sort((a, b) => {
        return a.name.localeCompare(b.name);
      }),
    [suggestions]
  );

  const popularUuids = useMemo(
    () =>
      [
        'd513e568-ad32-44b5-b0c8-1b7d3fbe88a6',
        'a9e5c921-6753-4dd5-bbf1-d3e781a53414',
        'f48d9932-2638-426a-9ca8-d84a6b78fd6e',
        'a5e99983-4836-42b0-9869-162d78db7524',
        '795bae79-af8a-43e9-8f76-c7d62378d388',
        'ef5f765a-9d26-4f73-88f5-b02c862a9a40',
        '7da5a261-bf51-4098-b6d9-1c8e0f813b08',
        '31a297df-b771-4c21-b673-2c57a9a2543b',
        '344ff9ed-80cd-4375-967e-453a74d6f0b5',
        '862eed5d-4b4d-43d2-9881-5cd62dd2dcf0',
        'c54c39a2-a979-4ef6-8ae9-3c1770fac7ee',
      ]
        .map((uuid) =>
          allSuggestions.find((suggestion) => suggestion.uuid === uuid)
        )
        .filter((item): item is Suggestions.Suggestion => item !== undefined),
    [allSuggestions]
  );

  const sortedSuggestions = useMemo(
    () => [
      ...popularUuids,
      ...allSuggestions.sort((a, b) => {
        return a.name.localeCompare(b.name);
      }),
    ],
    [popularUuids, allSuggestions]
  );

  const [debouncedInput, input, setInput] = useDebounce('');
  const [pendingInput, setPendingInput] = useState<string | null>(null);
  const suggestionsQuery = useSuggestionsQuery(debouncedInput);

  useEffect(() => {
    if (pendingInput) {
      setInput(pendingInput);
      setPendingInput(null);
    }
  }, [pendingInput, setInput]);

  const getOptionsFromSuggestions = useCallback(() => {
    const initialList = input
      ? suggestionsQuery.data
      : [...(sortedSuggestions || [])];
    const options = (initialList || [])
      .filter((suggestion) => {
        const isExcluded = excludedReports?.includes(suggestion.uuid);
        const isSelected = value?.some(
          (item): item is Searches.ISearchItem =>
            typeof item !== 'string' && item.value === suggestion.uuid
        );
        return !isExcluded && !isSelected;
      })
      .map((suggestion) => {
        const option: Searches.ISearchItem = {
          value: suggestion.uuid,
          name: suggestion.name,
          type: 'report',
        };

        return option;
      })
      .slice(0, 50);

    if (
      debouncedInput.length >= MIN_SEARCH_LENGTH &&
      debouncedInput.length <= MAX_SEARCH_LENGTH &&
      !suggestionsQuery.isLoading &&
      !reportsOnly
    ) {
      options.push({
        type: 'code',
        name: debouncedInput,
        value: debouncedInput,
      });
    }

    return options;
  }, [
    sortedSuggestions,
    debouncedInput,
    suggestionsQuery.isLoading,
    suggestionsQuery.data,
    reportsOnly,
    input,
    excludedReports,
    value,
  ]);

  const options: Searches.ISearchItem[] = useMemo(
    () => getOptionsFromSuggestions(),
    [getOptionsFromSuggestions]
  );

  const handleRemove = useCallback(
    (input: Searches.IStoredSearchItem) => {
      if (!value) {
        return;
      }
      const updatedValue = value.filter(
        (item): item is Searches.ISearchItem =>
          typeof item !== 'string' &&
          !(item.type === input.type && item.value === input.value)
      );
      if (onChange) {
        onChange(updatedValue);
      }
    },
    [value, onChange]
  );

  const handleCodeEdit = useCallback(
    (item: Searches.ISearchItem) => {
      handleRemove(item);
      setPendingInput(item.value);
    },
    [handleRemove]
  );

  const onBlurHandler = useCallback(
    (event: React.FocusEvent<HTMLDivElement, Element>) => {
      if (onBlur) {
        onBlur(event);
      }
      setFocused(false);
    },
    [onBlur]
  );

  const onFocusHandler = useCallback(() => {
    setFocused(true);
  }, []);

  const onKeyUpHandler = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (onKeyUp) {
        onKeyUp(event);
      }
    },
    [onKeyUp]
  );

  const codeSearchError = useMemo(() => {
    if (debouncedInput.length > MAX_SEARCH_LENGTH) {
      return `Code search cannot be more than ${MAX_SEARCH_LENGTH} characters`;
    }
    return undefined;
  }, [debouncedInput.length]);

  const shrink = !!input || focused || !!value?.length;
  const redirectToDropdownValues = useRedirectToDropdownValues();
  const onChangeHandler = useCallback(
    (
      event: SyntheticEvent<Element, Event>,
      newValue: (string | Searches.ISearchItem)[]
    ) => {
      // prevent invalid code search submissions
      if (
        newValue.find(
          (item) => typeof item === 'string' && item.length > MAX_SEARCH_LENGTH
        )
      ) {
        return;
      }

      const values = newValue.map((item) => {
        if (typeof item === 'string') {
          const code: Searches.ISearchItem = {
            type: 'code',
            value: item,
            name: item,
          };
          return code;
        }
        return item;
      });

      if (onChange) {
        // Controlled mode - call onChange with new values
        onChange([...values]);
      } else {
        // Uncontrolled mode - update local state
        setUncontrolledValue([...values]);
        redirectToDropdownValues(values);
        setDisabled(true);
      }
    },
    [onChange, redirectToDropdownValues, setDisabled]
  );

  return (
    <Autocomplete<Searches.ISearchItem, true, true, true>
      className="reports-dropdown"
      freeSolo
      selectOnFocus
      multiple
      clearOnBlur
      fullWidth
      openOnFocus
      handleHomeEndKeys
      blurOnSelect
      loading={suggestionsQuery.isLoading}
      inputValue={input}
      onInputChange={(event, newInputValue) => {
        setInput(newInputValue);
      }}
      onFocus={onFocusHandler}
      onBlur={onBlurHandler}
      filterOptions={(x) => x}
      options={options}
      value={value}
      disabled={disabled}
      onKeyUp={(event) => onKeyUpHandler(event)}
      onChange={onChangeHandler}
      getOptionLabel={(option) => {
        const label = typeof option === 'string' ? option : option.name;
        return label;
      }}
      renderOption={(props, option) => {
        const { ...rest } = props;
        const isReport = option.type === 'report';
        const suggestion = isReport
          ? sortedSuggestions.find(
              (suggestion) => suggestion.uuid === option.value
            )
          : undefined;
        const code = option.type === 'code' ? option.value : undefined;
        return (
          <OptionItem
            {...rest}
            key={`${option.type}-${option.value}`}
            suggestion={suggestion}
            code={code}
          />
        );
      }}
      ListboxComponent={ListBoxComponent}
      renderInput={(params) => {
        const { InputProps, ...textFieldProps } = params;

        return (
          <TextField
            {...textFieldProps}
            {...TextFieldProps}
            autoComplete="off"
            label={label}
            placeholder={placeholder}
            error={!!codeSearchError}
            helperText={codeSearchError}
            InputLabelProps={{
              shrink: shrink,
              style: {
                paddingLeft: shrink ? 0 : 30,
              },
            }}
            InputProps={{
              ...InputProps,
              autoFocus,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    {loading || suggestionsQuery.isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : (
                      <SearchIcon />
                    )}
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <React.Fragment>
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        );
      }}
      renderTags={(value: Searches.ISearchItem[], getTagProps) => {
        return value.map((option, index) => {
          const { ...tagProps } = getTagProps({ index });
          return (
            <React.Fragment key={`${option.type}-${option.value}`}>
              <ReportChip
                {...tagProps}
                key={`${option.type}-${option.value}`}
                item={option}
                onDelete={() => handleRemove(option)}
                onCodeEdit={(code) => {
                  handleCodeEdit(code);
                }}
              />
              <Typography variant="body1" color={grey[500]} sx={{ px: 1 }}>
                {operator}
                {index < value.length - 1 ? ' ' : '...'}
              </Typography>
            </React.Fragment>
          );
        });
      }}
      {...autoCompleteProps}
    />
  );
};

interface OptionItemProps extends MenuItemProps {
  suggestion?: Suggestions.Suggestion;
  code?: string;
}

const OptionItem = React.memo(
  ({ suggestion, code, ...props }: OptionItemProps) => {
    const placeholderImage = `${
      import.meta.env.VITE_STATIC_ASSETS_URL
    }/reports/0.jpg`;
    const image = suggestion
      ? `${import.meta.env.VITE_STATIC_ASSETS_URL}/reports/${
          suggestion.uuid
        }.jpg`
      : placeholderImage;
    return (
      <MenuItem {...props}>
        <ListItemAvatar>
          <LazyImage src={image} height="40" width="40" alt="" />
        </ListItemAvatar>
        <ListItemText
          primary={
            suggestion?.name || (
              <code style={{ marginBottom: '4px' }}>{code}</code>
            )
          }
          secondary={
            suggestion ? (
              <CategoryChip
                industry={suggestion.industry}
                category={suggestion.category}
                industries_id={suggestion.industries_id}
              />
            ) : (
              <CategoryChip code />
            )
          }
          primaryTypographyProps={{ component: 'span' }}
          secondaryTypographyProps={{ component: 'span' }}
        />
      </MenuItem>
    );
  }
);
OptionItem.displayName = 'OptionItem';

const ListBoxComponent = React.memo(
  forwardRef<
    HTMLUListElement,
    React.HTMLAttributes<HTMLUListElement> & { children?: React.ReactNode }
  >((props, ref) => {
    const { children, ...other } = props;
    return (
      <MenuList role="listbox" ref={ref} {...other}>
        {children}
      </MenuList>
    );
  })
);
ListBoxComponent.displayName = 'ListBoxComponent';

export const useRedirectToDropdownValues = () => {
  const router = useRouter();

  return useCallback(
    (values: Searches.ISearchItem[]) => {
      const [item] = values;
      if (!item) {
        return;
      }
      const search: Searches.SearchObject = {
        all: [
          {
            type: item.type,
            value: item.value,
            name: item.name,
          },
        ],
        any: [],
        none: [],
      };
      router.push({
        path: '/reports/new',
        query: {
          search: JSON.stringify(search),
        },
      });
    },
    [router]
  );
};
